import { sortBy } from "lodash-es";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

import type { IdMap } from "@/model/baseTypes";
import type { BoardCard, SearchResult } from "@/model/card";
import type { CardSearchResult } from "@/model/search";
import { asReadonly } from "@/utils/vue";

import { useBoardStore } from "../board";
import { useBoardsStore } from "../boards";

type Index = { index: number } | { id: string };

export const useSearchResultsStore = defineStore("searchResults", () => {
  const found = ref<CardSearchResult[]>([]);
  const foundIndex = ref(0);
  const backendSearching = ref(false);
  const backendResults = ref<SearchResult[]>([]);

  const currentResult = computed(() => found.value[foundIndex.value - 1]);
  const hasNoResults = computed(
    () => found.value.length === 0 && !backendSearching.value && backendResults.value.length === 0,
  );
  const hasBackendResults = computed(
    () => !backendSearching.value && backendResults.value.length > 0,
  );

  function search(cards: IdMap<BoardCard>, match: (card: BoardCard) => boolean) {
    const foundItemId = currentResult.value?.id;
    const res = new Array<CardSearchResult>();
    for (const id in cards) {
      if (match(cards[id])) {
        res.push(toSearchResult(cards[id]));
      }
    }
    found.value = sortSearchResults(res);
    const newFound = foundItemId ? found.value.findIndex((item) => item.id === foundItemId) + 1 : 0;
    if (found.value.length > 0) {
      foundIndex.value = calcIndex({ index: newFound });
    }
  }

  function calcIndex(c: Index) {
    if ("index" in c) {
      return c.index;
    }
    const index = found.value.findIndex((result) => result.id === c.id);
    return index < 0 ? foundIndex.value : index === foundIndex.value - 1 ? 0 : index + 1;
  }

  return {
    found: asReadonly(found),
    foundIndex: asReadonly(foundIndex),
    backendSearching: asReadonly(backendSearching),
    backendResults: asReadonly(backendResults),

    currentResult,
    hasNoResults,
    hasBackendResults,

    search,
    at: (index: number) => found.value[index - 1],
    setCurrent: (c: Index) => {
      if (found.value.length > 0) {
        foundIndex.value = calcIndex(c);
      }
    },
    unsetCurrent: () => (foundIndex.value = 0),
  };
});

export function sortSearchResults<T extends SearchResult | CardSearchResult>(results: T[]): T[] {
  return sortBy(results, (r) => r.text.toLowerCase());
}

function toSearchResult(boardCard: BoardCard): CardSearchResult {
  const boardId = useBoardStore().boardId();
  const card = boardCard.data;
  return {
    boardId,
    id: card.id,
    text: card.text,
    typeId: card.type.id,
    iterationId: card.iterationId || undefined,
    teamId: card.teamId || undefined,
    precondTeamId: card.precondTeam?.id,
    dependTeamId: card.dependTeam?.id,
    flagType: card.flagType,
    almId: card.almId,
    card: boardCard,
    artId: useBoardsStore().boardById(boardId).artId,
    status: card.status,
    objectives: card.objectives,
  };
}
